import { z } from 'zod';
import defaultsJson from './defaults.json' with { type: 'json' };
import { canonicalJson, sha256Hex } from './hash.ts';
import { configObjectSchema, configSchema, SEMVER_RE, type Config } from './schema.ts';
import type { ConfigTableRow } from './table.ts';

export const PACKAGE_NAME = '@ml/config';

export * from './schema.ts';
export * from './content.ts';
export { canonicalJson, sha256Hex } from './hash.ts';
export {
  parseConfigTable,
  rowsToObject,
  getPath,
  setPath,
  deletePath,
  CONFIG_TABLE_HEADER,
} from './table.ts';
export type { ConfigTableRow, TableBound } from './table.ts';

export class ConfigError extends Error {
  readonly issues: z.core.$ZodIssue[];
  constructor(issues: z.core.$ZodIssue[]) {
    super(
      `Config không hợp lệ:\n${issues.map((i) => `- ${i.path.join('.') || '(root)'}: ${i.message}`).join('\n')}`,
    );
    this.name = 'ConfigError';
    this.issues = issues;
  }
}

export type SafeParseConfigResult =
  { ok: true; config: Config } | { ok: false; error: ConfigError };

export function safeParseConfig(input: unknown): SafeParseConfigResult {
  const r = configSchema.safeParse(input);
  return r.success
    ? { ok: true, config: r.data }
    : { ok: false, error: new ConfigError(r.error.issues) };
}

/** Kiểm tra đủ key, đúng kiểu, min/max và ràng buộc chéo. Ném ConfigError nếu sai. */
export function parseConfig(input: unknown): Config {
  const r = safeParseConfig(input);
  if (!r.ok) throw r.error;
  return r.config;
}

/** Bản mặc định (defaults.json) đã qua parse; mỗi lần gọi trả bản sao mới. */
export function defaultConfig(): Config {
  return parseConfig(structuredCloneJson(defaultsJson));
}

function structuredCloneJson<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

type DeepPartial<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge(base: unknown, patch: unknown): unknown {
  if (!isPlainObject(base) || !isPlainObject(patch)) return patch === undefined ? base : patch;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) out[k] = deepMerge(base[k], v);
  return out;
}

/** Ghép bản vá (mảng/record thay nguyên khối) lên base (mặc định = defaults) rồi parse. */
export function mergeConfig(patch: DeepPartial<Config>, base: Config = defaultConfig()): Config {
  return parseConfig(deepMerge(structuredCloneJson(base), patch));
}

/** SHA-256 hex của JSON chuẩn hoá (khoá sắp xếp). Gồm cả configVersion. */
export function configHash(config: Config): string {
  return sha256Hex(canonicalJson(config));
}

/**
 * Định dạng phát hành (M2: lưu D1, cache KV, game tải GET /config/<version>):
 * JSON bất biến `{ configVersion, configHash, config }`.
 */
export const configReleaseSchema = z.strictObject({
  configVersion: z.string().regex(SEMVER_RE),
  configHash: z.string().regex(/^[0-9a-f]{64}$/),
  config: configObjectSchema,
});
export type ConfigRelease = { configVersion: string; configHash: string; config: Config };

export function makeRelease(config: Config): ConfigRelease {
  const parsed = parseConfig(config);
  return { configVersion: parsed.configVersion, configHash: configHash(parsed), config: parsed };
}

/** Parse bản phát hành: kiểm schema, ràng buộc chéo, version khớp và hash khớp nội dung. */
export function parseRelease(input: unknown): ConfigRelease {
  const env = configReleaseSchema.parse(input);
  const config = parseConfig(env.config);
  if (env.configVersion !== config.configVersion)
    throw new Error(`configVersion lệch: ${env.configVersion} ≠ ${config.configVersion}`);
  const hash = configHash(config);
  if (hash !== env.configHash) throw new Error(`configHash lệch: ${env.configHash} ≠ ${hash}`);
  return { configVersion: env.configVersion, configHash: hash, config };
}

type JsonNode = Record<string, unknown> & { properties?: Record<string, JsonNode> };

/**
 * JSON Schema (draft 2020-12) của config. Nếu truyền các dòng config-table thì gắn
 * description, default, x-live, x-milestone, x-type vào từng key (cho form admin M2).
 */
export function buildJsonSchema(rows?: readonly ConfigTableRow[]): Record<string, unknown> {
  const schema = z.toJSONSchema(configObjectSchema, { target: 'draft-2020-12' }) as JsonNode;
  schema.$id = 'https://magic-links.local/schema/config.json';
  schema.title = 'Magic Link - Dungeon live-config';
  for (const row of rows ?? []) {
    const node = jsonSchemaNode(schema, row.key);
    if (!node) throw new Error(`buildJsonSchema: key ${row.key} không có trong schema`);
    if (row.description) node.description = row.description;
    node.default = row.default;
    node['x-live'] = row.live;
    node['x-milestone'] = row.milestone;
    node['x-type'] = row.type;
  }
  return schema;
}

export function jsonSchemaNode(
  schema: Record<string, unknown>,
  path: string,
): JsonNode | undefined {
  let cur: JsonNode | undefined = schema as JsonNode;
  for (const part of path.split('.')) cur = cur?.properties?.[part];
  return cur;
}
